import {
  FILTERED_LISTING_MODULE_TYPE,
  SECONDARY_LISTING_MODULE_TYPE,
} from '@/app/lib/analytics/constants'

/**
 * `page_type` de esta PLP (enum de `dictionary/analytic-variables.md`) — mismo
 * criterio que Promociones y recintos: `plp` para que todos los listados caigan
 * en los mismos informes. De aquí se derivan `item_list_id` y el `component_id`
 * del toggle de vista, así que cambiarlo mueve los tres a la vez.
 */
export const ANALYTICS_PAGE_TYPE = 'plp'

/**
 * `page_subtype` fijo, con provincia concreta o sin ella: el diccionario
 * (`analytic-variables.md` → `page_subtype`) lista `bestsellers` como valor del
 * enum, y es lo que identifica a esta sección en los informes con un solo
 * corte. La provincia NO se refleja aquí (a diferencia de Promociones, que usa
 * `ciudad`): ya viaja en `page_hierarchy` (`lo-mas-vendido/madrid`) y en el
 * `filter_value` del `filter_change` de provincia, así que no se pierde el dato
 * y el subtipo no se solapa con el de otras PLP de ciudad.
 */
export const ANALYTICS_PAGE_SUBTYPE = 'bestsellers'

/** `module_name` del listado principal en todos los eventos de esta página. */
export const ANALYTICS_MODULE_NAME = 'listado_lo_mas_vendido'

/**
 * `item_list_id` = `<page_type>_<module_type>` (`Listado filtrado.csv` →
 * «Click en card del listado»). Se preserva del `view_item_list` al
 * `select_item` para poder reconstruir el funnel.
 *
 * ⚠️ Con esa fórmula el valor coincide con el del listado de Promociones (mismo
 * `page_type` y mismo `module_type`); lo que distingue a los dos listados es
 * `item_list_name` + `page_hierarchy`. Se mantiene la fórmula del CSV, ya
 * aplicada en Promociones, en vez de inventar un id propio.
 */
export const ANALYTICS_ITEM_LIST_ID = `${ANALYTICS_PAGE_TYPE}_${FILTERED_LISTING_MODULE_TYPE}`

/** Nombre descriptivo del listado. Literal estable: NO varía por provincia (esa
 * dimensión ya viaja en `page_hierarchy`), para no disparar la cardinalidad. */
export const ANALYTICS_ITEM_LIST_NAME = 'listado_de_lo_mas_vendido'

/**
 * `module_position` de los tres módulos de producto de la página, por su
 * posición vertical en el layout: el carril de vistos recientemente, el listado
 * principal y la franja de otras ciudades (en ese orden — ver `page.tsx`).
 *
 * Es la posición del SLOT en el layout, no el recuento de módulos realmente
 * renderizados: "Vistos recientemente" desaparece si el visitante no tiene
 * historial, y "otras ciudades" solo sale con provincia concreta. Si se contara
 * en tiempo de render, la posición del listado principal oscilaría entre 1 y 2
 * según una cookie, y la dimensión dejaría de ser comparable entre sesiones.
 */
export const ANALYTICS_RECENTLY_VIEWED_MODULE_POSITION = '1'
export const ANALYTICS_MODULE_POSITION = '2'
export const ANALYTICS_OTHER_CITIES_MODULE_POSITION = '3'

/** Listado secundario "Eventos en otras ciudades" (`OtherProvinceEvents`). */
export const ANALYTICS_OTHER_CITIES_MODULE_NAME = 'eventos_en_otras_ciudades'
export const ANALYTICS_OTHER_CITIES_ITEM_LIST_ID = `${ANALYTICS_PAGE_TYPE}_${SECONDARY_LISTING_MODULE_TYPE}`
export const ANALYTICS_OTHER_CITIES_ITEM_LIST_NAME = 'eventos_en_otras_ciudades'

/** Orden fijo de los 4 filtros mostrados en esta página (FR-018). */
export const FILTER_ORDER = ['province', 'genre', 'subgenre', 'date_range', 'min_price'] as const

/** Mapa clave del bloque en `filters` → clave real en `applied_filters`, para
 * `sanitizeAppliedFilters` (`@/app/lib/api/sanitizeAppliedFilters`, genérico y
 * compartido por cualquier página que consuma `/data` con filtros). `genre` es
 * la única asimetría de esta página: el bloque se llama `genre` pero el valor
 * aplicado se llama `genre_nicename`; `date_range` coincide. Sin entrada para
 * `min_price`: ese bloque retira de sus propios `items` la opción ya
 * aplicada (ver `LoMasVendidoFilters.tsx`), así que `sanitizeAppliedFilters`
 * la descartaría siempre como "no válida" — el valor de Precio se lee
 * directo de la URL (`useListingFieldFilters`), no de `applied_filters`. */
export const APPLIED_FILTER_KEYS_MAP = {
  genre: 'genre_nicename',
  subgenre: 'subgenre_nicename',
  date_range: 'date_range',
} as const

/** Nombre accesible del `LoadingOverlay` que pinta `GeoLocationSync` mientras
 * redirige a la provincia de la cookie `location` y recarga el listado —
 * mismo criterio que Promociones/Agenda. */
export const GEO_LOCATION_LOADING_LABEL = 'Cargando lo más vendido de tu ubicación'
